import { useState, type ReactNode } from "react";
import { LoaderIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useMejorarRedaccion, useResolverPropuesta } from "@/hooks/useAsistenciaDeRedaccion";
import { getApiErrorMessage } from "@/lib/api";
import type { CampoConAsistencia as CodigoDeCampo } from "@/lib/ia";
import { cn } from "@/lib/utils";

interface CampoConAsistenciaProps {
  id: string;
  /** Código del campo: decide qué contexto le suma el servidor al prompt. */
  campo: CodigoDeCampo;
  value: string;
  onChange: (valor: string) => void;
  /** Nulo mientras el informe no existe (pantalla de creación). */
  informeId?: number;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  /**
   * El textarea se estira hasta el alto de su contenedor (mínimo 150 px del
   * diseño v2): para las descripciones, que comparten fila con un panel más alto.
   */
  llenarAlto?: boolean;
  /** `outline` es el botón con borde del diseño v2; `ghost` es el de siempre. */
  variantDeMejora?: "ghost" | "outline";
  /** Clases extra del botón «Mejorar redacción» (el diseño lo dibuja más compacto). */
  claseDelBotonDeMejora?: string;
  /** Acciones de la misma fila, ANTES de «Mejorar redacción» (p. ej. «Redactar desde terreno»). */
  accionesAntes?: ReactNode;
  /** Acciones de la misma fila, DESPUÉS (p. ej. la casilla «Revisada por una persona»). */
  accionesDespues?: ReactNode;
}

/**
 * Un campo de texto largo con asistencia de redacción (RF-E2).
 *
 * ES UN COMPONENTE Y NO MARKUP REPETIDO porque el prototipo `18-08-2026` pone
 * este mismo control en CATORCE campos —descripciones de suelos, maquinaria,
 * construcciones, observaciones de comparables…—. Hoy existen dos; los demás
 * llegan con sus pantallas.
 *
 * LA PROPUESTA NO PISA EL TEXTO. Se muestra al lado y la persona decide: es el
 * invariante de todo lo que produce IA en este producto —nada se guarda como
 * verdad sin un acto humano—, y acá además es lo único razonable. El original
 * lo escribió quien está mirando, y es el único que puede ver si la reescritura
 * cambió lo que quería decir.
 *
 * El punto verde vive en el estado de esta pantalla y no en la base: RF-E2 lo
 * describe como "ese campo ya pasó por el proceso", y en la pantalla de
 * creación el informe todavía no existe, así que no habría dónde anclarlo.
 * Es la misma decisión del prototipo.
 */
export function CampoConAsistencia({
  id,
  campo,
  value,
  onChange,
  informeId,
  rows = 3,
  placeholder,
  disabled,
  llenarAlto = false,
  variantDeMejora = "ghost",
  claseDelBotonDeMejora,
  accionesAntes,
  accionesDespues,
}: CampoConAsistenciaProps) {
  const mejorar = useMejorarRedaccion();
  const resolver = useResolverPropuesta();

  const [propuesta, setPropuesta] = useState<{ id: number; texto: string } | null>(null);
  const [mejorado, setMejorado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const textoVacio = value.trim().length === 0;

  function pedirMejora() {
    setError(null);
    mejorar.mutate(
      { texto: value, campo, informeId },
      {
        onSuccess: ({ ejecucion_id, texto }) => setPropuesta({ id: ejecucion_id, texto }),
        // El error se muestra ACÁ y no en un toast: habla de este campo, y un
        // toast se va antes de que la persona vuelva a mirarlo.
        onError: (e) =>
          setError(
            getApiErrorMessage(e, "No pudimos mejorar el texto. Inténtalo de nuevo."),
          ),
      },
    );
  }

  /* Aceptar y descartar avisan los DOS al servidor. Un descarte informa tanto
     como una aceptación: sin él, la medición solo tendría los casos en que la
     IA acertó. Si ese aviso falla no se le dice nada a la persona — ya obtuvo
     lo que quería, y su trabajo no depende de que la auditoría se escriba. */
  function resolverPropuesta(resultado: "aceptado" | "descartado") {
    if (!propuesta) return;
    resolver.mutate({ ejecucionId: propuesta.id, resultado });
    setPropuesta(null);
  }

  return (
    <div className={cn(llenarAlto ? "flex h-full min-h-0 flex-1 flex-col gap-2" : "space-y-2")}>
      <Textarea
        id={id}
        rows={rows}
        className={cn(llenarAlto && "min-h-40 flex-1 resize-y")}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(evento) => onChange(evento.target.value)}
      />

      <div className={cn("flex flex-wrap items-center", llenarAlto ? "gap-2" : "gap-3")}>
        {accionesAntes}
        <Button
          type="button"
          variant={variantDeMejora}
          size="sm"
          className={claseDelBotonDeMejora}
          onClick={pedirMejora}
          // Sin texto no hay nada que corregir, y la llamada se paga igual.
          disabled={disabled || textoVacio || mejorar.isPending}
        >
          {mejorar.isPending ? <Spinner /> : <LoaderIcon className="size-4" strokeWidth={1.8} />}
          Mejorar redacción
        </Button>

        {mejorado ? (
          <span className="text-ink-hint flex items-center gap-2 text-xs font-bold">
            <span className="bg-success-dot size-1.5 rounded-full" aria-hidden="true" />
            Mejorado con IA
          </span>
        ) : null}
        {accionesDespues}
      </div>

      {/* `aria-live`: la propuesta aparece sin que la persona haya movido el
          foco, así que un lector de pantalla no se enteraría de otro modo. */}
      <div aria-live="polite">
        {mejorar.isPending ? (
          <p className="text-ink-hint text-xs">Generando sugerencia de redacción…</p>
        ) : null}

        {error ? <p className="text-destructive text-xs font-medium">{error}</p> : null}

        {propuesta ? (
          <div className="bg-info-soft space-y-3 rounded-xl p-4">
            <p className="text-info-soft-foreground text-xs font-bold">Sugerencia</p>
            {/* `pretty` porque es texto de lectura, no un dato. */}
            <p className="text-ink-body max-w-[70ch] text-sm text-pretty">{propuesta.texto}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  onChange(propuesta.texto);
                  setMejorado(true);
                  resolverPropuesta("aceptado");
                }}
              >
                Usar este texto
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => resolverPropuesta("descartado")}
              >
                Descartar
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
